/**
 * useParticipantSelection Hook
 * Manages participant selection (friends/groups) and tab state
 * Reuses groups data from React Query (useGroupsQuery) so Friends page and
 * Create flow share a single cache.
 */

import { useState, useCallback } from 'react';
import type { ParticipantTab } from '../types/create.types';
import type { Group } from '../../../types/challenges';
import { useGroupsQuery } from '../../friends/hooks/useGroupsQuery';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';

interface UseParticipantSelectionReturn {
  activeTab: ParticipantTab;
  setActiveTab: React.Dispatch<React.SetStateAction<ParticipantTab>>;
  selectedFriendId: string | null;
  setSelectedFriendId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedGroupId: string | null;
  setSelectedGroupId: React.Dispatch<React.SetStateAction<string | null>>;
  selectedGroup: Group | null;
  setSelectedGroup: React.Dispatch<React.SetStateAction<Group | null>>;
  availableGroups: Group[];
  groupsLoading: boolean;
  resetSelection: () => void;
}

export const useParticipantSelection = (currentUserId: string): UseParticipantSelectionReturn => {
  const { isSignedIn } = useSupabaseAuth();
  const [activeTab, setActiveTab] = useState<ParticipantTab>('friends');
  const [selectedFriendId, setSelectedFriendId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);

  // Reuse groups data and cache from Friends domain (skip when signed out — avoids guest pool-user fetches)
  const { data: groups = [], isLoading: groupsLoading } = useGroupsQuery(
    currentUserId,
    !!currentUserId && isSignedIn
  );
  const availableGroups = groups as Group[];

  const resetSelection = useCallback(() => {
    setSelectedGroupId(null);
    setSelectedGroup(null);
    setSelectedFriendId(null);
    setActiveTab('friends');
  }, []);

  return {
    activeTab,
    setActiveTab,
    selectedFriendId,
    setSelectedFriendId,
    selectedGroupId,
    setSelectedGroupId,
    selectedGroup,
    setSelectedGroup,
    availableGroups,
    groupsLoading,
    resetSelection,
  };
};

